// ── Early Bird 早期项目数据源 — GitHub 解析 + 公开联系方式提取 ──────────
// 数据源：docs.early-bird.space（ETHGlobal Showcase / Ethereum Foundation ESP）
// Tier 1 范围：只提取项目方自己公开披露、且披露目的就是「方便被联系到」的渠道
// （GitHub 公开资料邮箱 / Twitter / 个人站点 / 项目官网首页展示的邮箱），不做
// 电话/微信采集（见风险清单）。
//
// 2026-07-09 补充：之前"求联系方式"只查 GitHub 仓库 owner 的公开资料，很多项目
// （尤其 Colosseum 这类没有公开仓库的）其实把邮箱直接印在官网首页上（比如
// "founders@xyz.com"），但完全没被读取——用户反馈点了"求联系方式"什么都拿不到，
// 而网站上明明有联系方式。现在并行加一路：抓项目官网首页正文（复用 wiki 生成
// 已有的 fetchHomepageContent），正则提取邮箱，和 GitHub 资料合并，官网邮箱优先
// （项目方自己在首页主动展示，可信度和时效性都更高）。
//
// 2026-07-09 再补充：真实案例（Solomon Labs）官网是纯前端渲染 SPA，body 是空的，
// 上面这条邮箱提取路径完全找不到东西。但 fetchHomepageContent 已经会从 <head> 的
// twitter:site/twitter:creator meta 标签兜底解析出官方推特（服务端渲染的标准 SEO
// 标签，纯前端站点也不例外），这里直接复用那个信号作为联系渠道——不是邮箱，但是
// 真实可达的联系方式，好过"什么都没有"。
//
// 2026-07-09 再补充：官网自己链接了 LinkedIn 公司主页时，顺带拉一下团队成员名单
// （复用主分析流程已有的 BrightData 代理），把每个人的 LinkedIn 主页链接也放进联系
// 方式里。刻意只用"官网自己暴露的 LinkedIn 链接"这一路信号，不像 wiki 生成那样再
// 加一层网页搜索兜底——"求联系方式"是每次点击都会触发的高频操作，加网页搜索会让
// 每次点击都多付一次 Surf 调用，成本核算和 wiki 生成（按项目、较低频）不是一回事。
import { RD_BASE } from './config';
import { fetchHomepageContent } from './projectContext';
import { fetchLinkedInPeople, mapLinkedInEmployeesToFounders } from './teamEnhanced';

const ghFetch = (url) =>
  fetch(url, { signal: typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? AbortSignal.timeout(5000) : undefined })
    .then(r => r.status === 200 ? r.json() : null)
    .catch(() => null);

/**
 * 从 Early Bird 项目的 sourceUrl 解析出 GitHub 仓库
 * - sourceUrl 本身已是 GitHub 链接（如 Ethereum Foundation ESP 约 1/3 的项目）→ 直接解析
 * - 否则（如 ETHGlobal Showcase 的展示页链接）→ 走 /api/fetch-html 代理抓页面，
 *   从页面 HTML 里正则提取嵌入的 GitHub 仓库链接（展示页通常会放源码链接）
 */
export async function resolveGithubUrl(sourceUrl) {
  if (!sourceUrl) return null;

  const direct = sourceUrl.match(/github\.com\/([\w.-]+)\/([\w.-]+)/i);
  if (direct) {
    const owner = direct[1];
    const repo = direct[2].replace(/\.git$/, '');
    return { owner, repo, url: `https://github.com/${owner}/${repo}` };
  }

  try {
    const res = await fetch(`${RD_BASE}/api/fetch-html?url=${encodeURIComponent(sourceUrl)}`, {
      signal: typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? AbortSignal.timeout(10000) : undefined,
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.ok || !data.html) return null;
    const m = data.html.match(/https?:\/\/github\.com\/([\w.-]+)\/([\w.-]+)/i);
    if (!m) return null;
    const owner = m[1];
    const repo = m[2].replace(/\.git$/, '');
    return { owner, repo, url: `https://github.com/${owner}/${repo}` };
  } catch { return null; }
}

/**
 * 提取仓库 owner 的公开联系方式（GitHub 个人资料里自愿填写的字段）
 * 只读 GitHub 用户自己选择公开的 email / twitter_username / blog（个人站点）——
 * 不做邮箱猜测、不查第三方联系人富化服务。三个字段全空时返回 null。
 */
export async function fetchGithubPublicContact(owner, repo) {
  if (!owner || !repo) return null;

  const repoInfo = await ghFetch(`https://api.github.com/repos/${owner}/${repo}`);
  const ownerLogin = repoInfo?.owner?.login || owner;
  const ownerType = repoInfo?.owner?.type || null; // 'User' | 'Organization'

  const user = await ghFetch(`https://api.github.com/users/${ownerLogin}`);
  if (!user) return null;

  const email = user.email || null;
  const twitterUsername = user.twitter_username || null;
  const website = user.blog || null;

  if (!email && !twitterUsername && !website) return null;

  return {
    login: ownerLogin,
    ownerType,
    email,
    twitterUsername,
    website,
    source: 'github-public-profile',
  };
}

// 常见占位符邮箱（模板/示例站点里常见的假邮箱），过滤掉避免误报
const PLACEHOLDER_EMAIL_RE = /^(you|your|user|name|email|example|test|hello|contact)@(example|domain|yoursite|yourcompany|company|email)\.(com|org|xyz|io)$/i;

function extractEmailFromText(text) {
  if (!text) return null;
  const matches = text.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g);
  if (!matches) return null;
  return matches.find(m => !PLACEHOLDER_EMAIL_RE.test(m)) || null;
}

function extractTwitterHandleFromUrl(url) {
  if (!url) return null;
  const m = String(url).match(/(?:twitter|x)\.com\/([a-zA-Z0-9_]{2,15})(?:[/?#]|$)/i);
  return m ? m[1] : null;
}

/**
 * 官网 footer 有链接到 LinkedIn 公司主页时，拉团队成员名单（名字+职位+个人 LinkedIn
 * 链接），失败/无结果时静默返回空数组，不影响其它联系方式渠道展示。
 */
async function fetchTeamLinkedInProfiles(linkedinCompanyUrl) {
  if (!linkedinCompanyUrl) return [];
  const result = await fetchLinkedInPeople(linkedinCompanyUrl).catch(() => null);
  if (!result || result.error || result.pending) return [];
  const founders = mapLinkedInEmployeesToFounders(result.employees);
  return founders.filter(f => f.linkedin).map(f => ({ name: f.name, role: f.role, linkedin: f.linkedin }));
}

/**
 * 从项目官网首页提取联系方式：正文里主动展示的邮箱 + <head> 里的官方推特（后者
 * 对纯前端渲染的 SPA 尤其重要，body 抓不到东西时它是唯一能拿到的真实联系渠道）+
 * 团队成员的 LinkedIn 主页链接（官网自己链接了公司主页才会触发）。
 * 复用主分析流程已有的 fetchHomepageContent，这里只做提取，不做 AI 解读。
 */
async function fetchWebsiteContact(projectLiveUrl) {
  if (!projectLiveUrl) return null;
  const content = await fetchHomepageContent(projectLiveUrl).catch(() => null);
  const email = extractEmailFromText(content?.compressedText);
  const twitterUsername = extractTwitterHandleFromUrl(content?.links?.twitterUrl);
  const team = await fetchTeamLinkedInProfiles(content?.links?.linkedinCompanyUrl);
  if (!email && !twitterUsername && !team.length) return null;
  return { email, twitterUsername, website: projectLiveUrl, team };
}

/**
 * Colosseum 项目主页（colosseum.com/companies/{slug}）是服务端渲染的（SvelteKit
 * SSR），Website/X 推特链接和团队成员的 Colosseum 社区主页链接直接就在原始 HTML
 * 里，完全免费、不需要认证——跟项目自己的官网（经常是纯前端渲染 SPA，抓不到任何
 * body 内容）比起来是个便宜很多的数据源。project.id 是 "colosseum-{slug}" 格式，
 * 反推出 slug 直接拼 URL，不需要额外存储这个链接。不是每个项目都填全了这些字段
 * （有的只填网站，没填推特/团队），能拿到什么算什么，都拿不到时返回 null。
 * 注意页面上还有 Colosseum 自己的社交账号链接（比如 x.com/colosseum），必须只匹配
 * "Website"/"X / Twitter" 这两个按钮本身的链接，不能扫全页所有 x.com 链接。
 */
export async function fetchColosseumProfile(project) {
  if (project?.organization !== 'Colosseum' || !String(project?.id || '').startsWith('colosseum-')) return null;
  const slug = project.id.slice('colosseum-'.length);
  try {
    const res = await fetch(`${RD_BASE}/api/fetch-html?url=${encodeURIComponent(`https://colosseum.com/companies/${slug}`)}`, {
      signal: typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? AbortSignal.timeout(10000) : undefined,
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data?.ok || !data.html) return null;
    const html = data.html;

    const websiteMatch = html.match(/<a href="([^"]+)"[^>]*>(?:(?!<\/a>)[\s\S])*?\bWebsite\s*<\/a>/i);
    const twitterMatch = html.match(/<a href="(https?:\/\/(?:twitter\.com|x\.com)\/[^"]+)"[^>]*>(?:(?!<\/a>)[\s\S])*?X\s*\/\s*Twitter\s*<\/a>/i);
    const teamHandles = [...new Set([...html.matchAll(/arena\.colosseum\.org\/profiles\/([a-zA-Z0-9_.-]+)/gi)].map(m => m[1]))];
    const team = teamHandles.map(handle => ({ handle, profileUrl: `https://arena.colosseum.org/profiles/${handle}` }));

    if (!websiteMatch && !twitterMatch && !team.length) return null;
    return {
      profileUrl: `https://colosseum.com/companies/${slug}`,
      website: websiteMatch ? websiteMatch[1] : null,
      twitterUrl: twitterMatch ? twitterMatch[1] : null,
      team,
    };
  } catch { return null; }
}

/**
 * 组合入口：给一个 Early Bird 项目的 sourceUrl + 官网地址 + 项目本身（用于反推
 * Colosseum 主页），三路并行解析联系方式——GitHub 仓库 owner 的公开资料 + 项目
 * 官网首页展示的邮箱/推特/团队 LinkedIn + Colosseum 主页的网站/推特/团队确认。
 * 优先级：官网/GitHub 找到的信号优先于 Colosseum（后者是项目方自己在 Colosseum
 * 提交表单里填的，可能没跟官网同步更新）。全部都找不到时 contact 为 null（调用方
 * 按「暂无数据源」处理，不是「查询失败」）。
 */
export async function resolveEarlyBirdContact(sourceUrl, projectLiveUrl, project) {
  const [gh, websiteContact, colosseumProfile] = await Promise.all([
    resolveGithubUrl(sourceUrl),
    fetchWebsiteContact(projectLiveUrl),
    fetchColosseumProfile(project),
  ]);
  const ghContact = gh ? await fetchGithubPublicContact(gh.owner, gh.repo) : null;

  const email = websiteContact?.email || ghContact?.email || null;
  const twitterUsername = ghContact?.twitterUsername || websiteContact?.twitterUsername
    || extractTwitterHandleFromUrl(colosseumProfile?.twitterUrl) || null;
  const website = ghContact?.website || websiteContact?.website || colosseumProfile?.website || null;
  const team = websiteContact?.team || [];
  const colosseumTeam = colosseumProfile?.team || [];
  const hasContact = !!(email || twitterUsername || website || team.length || colosseumTeam.length);

  return {
    githubUrl: gh?.url || null,
    owner: gh?.owner || null,
    repo: gh?.repo || null,
    contact: hasContact ? { email, twitterUsername, website, team, colosseumTeam } : null,
  };
}
